import { useCallback, useEffect, useMemo } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import "../graphql/queries"

import type { DashboardTaskCounts } from "../types"

import { GetDashboardTaskCountsDocument, type GetDashboardTaskCountsQuery } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseGetDashboardTaskCountsParams {
  assignedToId?: string
}

interface UseGetDashboardTaskCountsReturn {
  taskCounts: DashboardTaskCounts
  isFetching: boolean
  error: CombinedError | undefined
  refetch: () => void
}

export const useGetDashboardTaskCounts = ({
  assignedToId,
}: UseGetDashboardTaskCountsParams = {}): UseGetDashboardTaskCountsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ data, fetching, error }, reexecuteQuery] = useQuery<GetDashboardTaskCountsQuery>({
    query: GetDashboardTaskCountsDocument,
    variables: { assignedToId },
    // AK-4585 AC #6: Matches rows' network-only policy (createSearchStore) so counts don't flash a
    // stale cached value after approve/deny.
    requestPolicy: "network-only",
  })

  const refetch = useCallback(() => {
    reexecuteQuery()
  }, [reexecuteQuery])

  const output = data?.dashboard.taskCounts

  useEffect(() => {
    if (output?.__typename === "DashboardTaskCountsFailure") {
      notify({ type: NotificationType.Error, message: output.error.message })
    }
  }, [output, notify])

  const taskCounts = useMemo<DashboardTaskCounts>(() => {
    if (output?.__typename === "DashboardTaskCountsSuccess") {
      return {
        ...output,
        total: output.priceApprovals + output.qualityApprovals + output.sourceChangeApprovals + output.sampleRequests,
      }
    }
    return { priceApprovals: 0, qualityApprovals: 0, sourceChangeApprovals: 0, sampleRequests: 0, total: 0 }
  }, [output])

  return { taskCounts, isFetching: fetching, error, refetch }
}
